// Thumbnail generation (concurrency-limited, viewport-prioritised) + lazy
// load-on-approach of cell media, backed by a persistent WebP thumbnail cache.
//
// Thumbnails are WebP encoded once from the full-res original and stored in
// CacheStorage (`ms-thumbs-w768-v1`), so reopening a folder serves them without
// re-reading/re-encoding the originals. We deliberately do NOT call
// `navigator.storage.persist()` — the store is best-effort, so the browser may
// evict the whole origin bucket under storage pressure. Because that eviction is
// per-origin all-or-nothing (never per-entry), we keep our own bound: each entry
// is stamped with `X-Size`/`X-Created` and `pruneToBudget` deletes oldest-first
// past BUDGET on startup.
//
// Cache key is composite — `name + size + lastModified` (URL-encoded into a
// synthetic `/__thumb/...` Request). Size+mtime alone collide too easily (a
// folder of same-size files copied in one go shares an mtime tick), and the name
// kills that class. The name in the key means an in-app rename would orphan the
// entry, so `renameThumb` migrates it to the new key (see below); only an
// external/offline rename ever recaches.

/* ---------- Lazy media loading (load-once, no unload) ---------- */
// Thumbnail *generation* is eager for the whole folder — `preloadAll` enqueues
// every cell at render time so files you never scroll to still get generated
// and persisted (the viewport only decides the order, not whether). But
// *attaching* a cell's decoded <img> stays lazy: we attach it as the cell comes
// within a screen of the viewport, so it's ready by the time it scrolls in. We
// deliberately do NOT unload it again when it scrolls away: a decoded thumbnail
// bitmap is small and the browser's own image cache already evicts off-screen
// decodes under memory pressure, so a manual unload only bought re-decode churn
// (and flashes) on scroll-back for no real memory win. Two in-memory costs grow
// with the folder, both deliberate: the decoded <img> bitmaps (the larger one,
// but viewport-bounded — only attached cells hold one) and, since preloadAll,
// one object URL per file kept in thumbCache for the session (its encoded WebP
// blob — the tradeoff for instant scroll-in). The on-disk copies in CacheStorage
// are bounded by pruneToBudget; the in-memory object URLs are not.
const LOAD_MARGIN = '800px';
// Cells currently within a screen of the viewport. The browser maintains this
// set for us off the main thread (no getBoundingClientRect, no forced reflow),
// and it doubles as the generation-priority signal: pumpGen generates a queued
// cell that's in here before any that isn't (see nextGenIndex).
const visible = new Set();
const loadObserver = new IntersectionObserver((entries) => {
  for (const e of entries) {
    if (e.isIntersecting) { visible.add(e.target); loadCell(e.target); }
    else visible.delete(e.target);
  }
}, { rootMargin: LOAD_MARGIN });
export const io = {
  observe(cell) { loadObserver.observe(cell); },
  disconnectAll() { loadObserver.disconnect(); visible.clear(); },
};

// Eagerly warm the thumbnail cache for EVERY cell, not just those the
// IntersectionObserver has reached. Generation stays viewport-prioritised: the
// queue picker (nextGenIndex) generates on-screen cells first, so the rest just
// drain top-to-bottom behind them. Dedupes through the same session map as
// loadCell (cachedThumb is idempotent per name), so a cell that later scrolls
// into view reuses this generation and its src attaches instantly. Does NOT set
// cell._media.src here — loadCell (IntersectionObserver) stays the only place a
// decoded <img> is attached, keeping decoded bitmaps bounded to the viewport.
// The swallowed rejection mirrors loadCell's failure handling: a rejected entry
// self-evicts from thumbCache, so the cell's own loadCell retries when it
// scrolls in. Called once per render.
export function preloadAll(cells) {
  for (const cell of cells) {
    if (cell._item) cachedThumb(cell._item, cell).catch(() => {});
  }
}

// Session cache: name -> Promise<{ url, key }>. The object URL of the decoded
// WebP thumbnail plus the CacheStorage key it lives under (kept so renameThumb
// can migrate the persistent entry). Inserted synchronously (before the first
// await) so concurrent grid/preview/viewer misses for one name dedupe onto a
// single generation instead of racing. A rejected entry (e.g. a video that
// failed to decode) evicts itself so it can be retried, never cached as failure.
const thumbCache = new Map();
// name -> natural width/height, set once a thumb resolves (from the stored
// X-Ratio header on a hit, or the generator on a miss); kept for the session
// (cleared alongside thumbCache in clearSession) so rows-mode --ar never has
// to be recomputed for a name already seen.
const thumbRatio = new Map();
const THUMB_W = 768;
const WEBP_QUALITY = 0.8;

/* ---------- Persistent thumbnail store (CacheStorage) ---------- */
const CACHE_NAME = 'ms-thumbs-w768-v1';   // bump on any width/quality/format change
const BUDGET = 256 * 1024 * 1024;         // ~256 MB of encoded thumbnails
let putsDisabled = false;                 // flipped on a quota (or any put) failure

// Resolved once: the opened Cache, or null where CacheStorage is unavailable /
// throws (older/insecure contexts) — then everything degrades to session-only.
let storeP;
function store() {
  if (storeP === undefined) {
    storeP = (async () => {
      try { return self.caches ? await caches.open(CACHE_NAME) : null; }
      catch { return null; }
    })();
  }
  return storeP;
}

function keyFor(name, file) {
  return `/__thumb/${encodeURIComponent(name)}/${file.size}-${file.lastModified}`;
}

async function putThumb(st, key, blob, ratio) {
  if (putsDisabled) return;
  try {
    await st.put(key, new Response(blob, { headers: {
      'Content-Type': 'image/webp',
      'X-Ratio': String(ratio),
      'X-Created': String(Date.now()),
      'X-Size': String(blob.size),
    } }));
  } catch {
    putsDisabled = true;   // quota exceeded (or worse) — stop trying this session
  }
}

// Delete stale cache versions, then trim the current cache to BUDGET. Runs once
// at startup, fire-and-forget; guarded so it never blocks or throws into startup.
async function maintainCache() {
  try {
    if (!self.caches) return;
    const names = await caches.keys();
    await Promise.all(names
      .filter(n => n.startsWith('ms-thumbs-') && n !== CACHE_NAME)
      .map(n => caches.delete(n)));
  } catch {}
  try {
    const st = await store();
    if (st) await pruneToBudget(st);
  } catch {}
}

async function pruneToBudget(st) {
  const reqs = await st.keys();
  const entries = [];
  let total = 0;
  for (const req of reqs) {
    const res = await st.match(req);
    if (!res) continue;
    const size = +res.headers.get('X-Size') || 0;
    entries.push({ req, size, created: +res.headers.get('X-Created') || 0 });
    total += size;
  }
  if (total <= BUDGET) return;
  entries.sort((a, b) => a.created - b.created);   // oldest first
  for (const e of entries) {
    if (total <= BUDGET) break;
    try { await st.delete(e.req); total -= e.size; } catch {}
  }
}
maintainCache();

/* ---------- Cached-or-generate ---------- */
// The one place the cache contract lives: session map → persistent store →
// generate. Grid cells, preview slots and viewer peeks all resolve thumbnails
// through it. Returns the object URL of the WebP thumbnail.
export function cachedThumb(item, cell) {
  let entry = thumbCache.get(item.name);
  if (!entry) {
    entry = resolveThumb(item, cell);
    // Never cache a failure: drop a rejected entry so a later view retries.
    entry.catch(() => { if (thumbCache.get(item.name) === entry) thumbCache.delete(item.name); });
    thumbCache.set(item.name, entry);   // synchronous insert dedupes concurrent misses
  }
  return entry.then(e => e.url);
}

async function resolveThumb(item, cell) {
  const file = await item.handle.getFile();
  const key = keyFor(item.name, file);
  const st = await store();
  if (st) {
    try {
      const res = await st.match(key);
      const ratio = res ? +res.headers.get('X-Ratio') : 0;
      if (ratio > 0) {   // a hit with a missing/garbage X-Ratio is treated as a miss (no decode path)
        thumbRatio.set(item.name, ratio);
        return { url: URL.createObjectURL(await res.blob()), key };
      }
    } catch {}
  }
  const { blob, ratio } = await genThumb(item, cell, file);
  thumbRatio.set(item.name, ratio);
  // Await the put (not fire-and-forget): renameThumb's migration reads this
  // entry's key and match()es it, so the persistent entry must exist first.
  if (st) await putThumb(st, key, blob, ratio);
  return { url: URL.createObjectURL(blob), key };
}

// Directory switch: drop the whole session layer — it's keyed by bare name, so
// entries from the previous folder would collide with same-named files in the
// next one — and revoke its object URLs so hopping between folders doesn't
// accumulate encoded-WebP blobs for the rest of the session. The persistent
// store is untouched (its keys carry size+mtime, safe across folders).
export function clearSession() {
  const old = [...thumbCache.values()];
  thumbCache.clear();
  thumbRatio.clear();
  for (const p of old) p.then(e => URL.revokeObjectURL(e.url)).catch(() => {});
}

// Generate + persist a thumbnail iff the persistent store lacks it, without
// touching the session name-map — safe for items in directories other than the
// current one (tree.js's recursive batch generation). Rides the shared gen
// queue with cell=null, so on-screen cells always take priority over batch
// entries. 'skip' = session-only mode (nothing to persist into).
export async function ensureThumb(item) {
  const st = await store();
  if (!st || putsDisabled) return 'skip';
  const file = await item.handle.getFile();
  const key = keyFor(item.name, file);
  if (await st.match(key)) return 'hit';
  const { blob, ratio } = await genThumb(item, null, file);
  await putThumb(st, key, blob, ratio);
  return 'gen';
}

// On rename, hand the session entry over to the new name and migrate the
// persistent cache entry to the new (name-based) key so it isn't regenerated.
// The migration is fire-and-forget: it reads the original key off the (now
// re-keyed) session entry and computes the new key from a fresh getFile() —
// correct whether the rename preserved mtime (handle.move) or changed it
// (copy+delete fallback). If nothing was cached to migrate, it regenerates on
// next view. `handle` is the file's handle after the rename completed.
export function renameThumb(oldName, newName, handle) {
  const entry = thumbCache.get(oldName);
  if (entry) {
    thumbCache.delete(oldName);
    thumbCache.set(newName, entry);
  }
  const r = thumbRatio.get(oldName);
  if (r) {
    thumbRatio.delete(oldName);
    thumbRatio.set(newName, r);
  }
  migrateCacheEntry(newName, handle, entry);
}

// Cross-directory variant: migrate the persistent entry like renameThumb, but
// DELETE the session entry instead of re-keying it — the session map belongs
// to the source (current) folder, and `newName` is unique in the destination,
// not here: re-keying could clobber the entry of an unrelated local file that
// happens to carry that name.
export function moveThumb(oldName, newName, handle) {
  const entry = thumbCache.get(oldName);
  thumbCache.delete(oldName);
  thumbRatio.delete(oldName);
  migrateCacheEntry(newName, handle, entry);
}

async function migrateCacheEntry(newName, handle, entry) {
  const st = await store();
  if (!st || putsDisabled) return;
  try {
    const oldKey = entry ? (await entry).key : null;
    const file = await handle.getFile();
    const newKey = keyFor(newName, file);
    if (!oldKey || oldKey === newKey) return;
    const res = await st.match(oldKey);
    if (!res) return;
    try { await st.put(newKey, res); }
    catch { putsDisabled = true; return; }   // quota — stop trying (mirrors putThumb)
    await st.delete(oldKey);
  } catch {}
}

async function loadCell(cell) {
  if (cell._loading) return;
  cell._loading = true;
  try {
    // Pass the cell so its generation is prioritised while it's on-screen (it's
    // in the `visible` set here — the observer added it just before calling us).
    const url = await cachedThumb(cell._item, cell);
    if (cell.isConnected && cell._media.getAttribute('src') !== url) cell._media.src = url;
    applyRatio(cell);
  } catch (e) {
    console.warn('thumb failed', cell._item.name, e);
    // A transient getFile() failure (real File System Access handles can throw
    // where OPFS never does — a locked file, a cloud placeholder not yet
    // hydrated, a USB hiccup) leaves the cell blank, and the IntersectionObserver
    // won't refire for a cell that stays in view. cachedThumb already evicted the
    // rejected entry, so re-observing (which re-delivers the current intersection)
    // retries it; capped backoff so a permanently-bad file gives up.
    if ((cell._retries = (cell._retries || 0) + 1) <= 3) {
      setTimeout(() => {
        if (cell.isConnected) { loadObserver.unobserve(cell); loadObserver.observe(cell); }
      }, 1000 * cell._retries);
    }
  } finally {
    cell._loading = false;
  }
}

// Sets the rows-mode --ar custom property from thumbRatio, if known yet.
function applyRatio(cell) {
  const ar = thumbRatio.get(cell._item.name);
  if (ar) cell.style.setProperty('--ar', ar);
}
// A cell whose thumb is already cached (mode just switched to rows, or the
// same folder reopened) won't refire the lazy-load IntersectionObserver that
// normally calls applyRatio — layout.js calls this explicitly in those cases.
export function backfillRatios(cells) {
  for (const cell of cells) applyRatio(cell);
}

/* ---------- Thumbnail generation (concurrency-limited, viewport-prioritised) ---------- */
let genActive = 0; const genQueue = []; const MAX_GEN = 3;
// Count of generations actually started (cache misses). Exported for tests to
// assert that a reopen serves everything from the persistent cache.
export let genCount = 0;

// Resolves to { blob, ratio } — the WebP thumbnail and its aspect ratio.
// Internal: reached only via cachedThumb and ensureThumb. `cell` is the grid cell
// this thumb belongs to, used only to check viewport visibility when choosing
// what to generate next; an entry with no known cell just drains in background
// (top-to-bottom) order.
function genThumb(item, cell, file) {
  return new Promise((resolve, reject) => { genQueue.push({ item, file, resolve, reject, cell }); pumpGen(); });
}

// Index of the next queued entry to generate: the first (topmost — the queue is
// in render/top-to-bottom order) entry that's on-screen. If nothing on-screen is
// queued, index 0, so the background drains the folder top-to-bottom. Membership
// is a Set lookup — no rect reads, no reflow. `vis` is passed in so this stays a
// pure function for tests.
export function nextGenIndex(queue, vis) {
  for (let i = 0; i < queue.length; i++) {
    if (vis.has(queue[i].cell)) return i;
  }
  return 0;
}

function pumpGen() {
  while (genActive < MAX_GEN && genQueue.length) {
    const { item, file, resolve, reject } = genQueue.splice(nextGenIndex(genQueue, visible), 1)[0];
    genActive++;
    genCount++;
    (item.kind === 'video' ? videoThumb(file) : imageThumb(file))
      .then(resolve, reject)
      .finally(() => { genActive--; pumpGen(); });
  }
}
async function imageThumb(file) {
  const bmp = await createImageBitmap(file, {
    resizeWidth: THUMB_W, resizeQuality: 'high', imageOrientation: 'from-image',
  });
  const ratio = bmp.width / bmp.height;
  const canvas = new OffscreenCanvas(bmp.width, bmp.height);
  canvas.getContext('2d').drawImage(bmp, 0, 0);
  bmp.close();
  const blob = await canvas.convertToBlob({ type: 'image/webp', quality: WEBP_QUALITY });
  return { blob, ratio };
}
async function videoThumb(file) {
  const url = URL.createObjectURL(file);
  const v = document.createElement('video');
  v.muted = true; v.preload = 'auto'; v.src = url;
  try {
    await once(v, 'loadeddata', 15000);
    try { v.currentTime = Math.min(0.1, (v.duration || 1) / 2); await once(v, 'seeked', 8000); } catch {}
    const ratio = (v.videoWidth || 1) / (v.videoHeight || 1);
    const w = THUMB_W, h = Math.max(1, Math.round(THUMB_W * (v.videoHeight || 1) / (v.videoWidth || 1)));
    const canvas = new OffscreenCanvas(w, h);
    canvas.getContext('2d').drawImage(v, 0, 0, w, h);
    const blob = await canvas.convertToBlob({ type: 'image/webp', quality: WEBP_QUALITY });
    return { blob, ratio };
  } finally {
    v.removeAttribute('src'); v.load?.(); URL.revokeObjectURL(url);
  }
}
function once(el, ev, timeout) {
  return new Promise((res, rej) => {
    const t = timeout && setTimeout(() => { done(); rej(new Error('timeout: ' + ev)); }, timeout);
    const ok = () => { done(); res(); };
    const err = () => { done(); rej(new Error('error: ' + ev)); };
    function done() { clearTimeout(t); el.removeEventListener(ev, ok); el.removeEventListener('error', err); }
    el.addEventListener(ev, ok); el.addEventListener('error', err);
  });
}

/* ---------- Test-only hooks ---------- */
// Clear the in-memory session cache so the next cachedThumb goes through the
// persistent store (used by the regression suite to prove reopen = cache hits).
export function _clearMemCache() { thumbCache.clear(); thumbRatio.clear(); }
// True once no generation is in flight and the queue is drained — the suite
// polls this (not a genCount-stability heuristic) to know an open has settled.
export function genIdle() { return genActive === 0 && genQueue.length === 0; }
